/**
 * Manager Time Clock Portal
 * 
 * Allows managers to:
 * - View all employees' timesheets
 * - Approve/reject time entries
 * - Edit time entries (with audit trail)
 * - View payroll summaries
 * - Export reports
 * 
 * Regular users CANNOT access this screen.
 */

import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
    Clock,
    Users,
    Calendar,
    DollarSign,
    CheckCircle,
    XCircle,
    Edit,
    Save,
    X,
    Download,
    ChevronRight,
    ChevronDown,
    Filter,
    Search,
    AlertTriangle,
    User,
    MapPin,
    Car,
    FileText,
    BarChart3,
    TrendingUp,
    History,
    Check,
    Trash2,
    RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    timeClockService,
    TimeEntry,
    TimesheetSummary
} from '../utils/services/timeClockService';

interface ManagerTimeClockProps {
    managerId: string;
    managerName: string;
    companyId: string;
    onClose: () => void;
}

export default function ManagerTimeClock({
    managerId,
    managerName,
    companyId,
    onClose
}: ManagerTimeClockProps) {
    // State
    const [activeTab, setActiveTab] = useState<'overview' | 'pending' | 'all' | 'reports'>('overview');
    const [summaries, setSummaries] = useState<TimesheetSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
    const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState<'week' | 'biweekly' | 'month'>('week');
    const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
    const [editForm, setEditForm] = useState({
        clockIn: '',
        clockOut: '',
        mileage: '',
        notes: '',
        reason: ''
    });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

    // Load data
    useEffect(() => {
        loadTimesheets();
    }, [dateRange]);

    const loadTimesheets = async () => {
        setLoading(true);
        setIsRefreshing(true);
        try {
            const endDate = new Date();
            const startDate = new Date();

            switch (dateRange) {
                case 'week':
                    startDate.setDate(startDate.getDate() - 7);
                    break;
                case 'biweekly':
                    startDate.setDate(startDate.getDate() - 14);
                    break;
                case 'month':
                    startDate.setMonth(startDate.getMonth() - 1);
                    break;
            }

            const data = await timeClockService.getTimesheetSummaries(startDate, endDate);
            setSummaries(data);
        } catch (error) {
            console.error('Failed to load timesheets:', error);
            toast.error('Failed to load employee timesheets');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Calculate totals
    const totals = summaries.reduce((acc, s) => ({
        totalHours: acc.totalHours + s.totalHours,
        regularHours: acc.regularHours + s.regularHours,
        overtimeHours: acc.overtimeHours + s.overtimeHours,
        totalMileage: acc.totalMileage + s.totalMileage,
        estimatedPay: acc.estimatedPay + s.estimatedPay,
        pendingApproval: acc.pendingApproval + s.pendingApproval,
        approved: acc.approved + s.approved,
        entriesCount: acc.entriesCount + s.entriesCount,
    }), {
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0,
        totalMileage: 0,
        estimatedPay: 0,
        pendingApproval: 0,
        approved: 0,
        entriesCount: 0,
    });

    // Filter summaries
    const filteredSummaries = summaries.filter(s =>
        s.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Approve entry
    const handleApprove = async (userId: string, entryId: string) => {
        setProcessingIds(prev => new Set(prev).add(entryId));
        try {
            await timeClockService.approveTimeEntry(userId, entryId, managerId);
            toast.success('Time entry approved');

            // Optimistic update
            setSummaries(prev => prev.map(s => {
                if (s.userId !== userId) return s;
                return {
                    ...s,
                    entries: s.entries.map(e => e.id === entryId ? { ...e, status: 'approved' } : e),
                    pendingApproval: s.pendingApproval - 1,
                    approved: s.approved + 1
                };
            }));
        } catch (error) {
            console.error('Failed to approve entry:', error);
            toast.error('Failed to approve entry');
            loadTimesheets(); // Fallback reload
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(entryId);
                return next;
            });
        }
    };

    // Reject entry
    const handleReject = async (userId: string, entryId: string) => {
        const reason = prompt('Enter rejection reason:');
        if (!reason) return;

        setProcessingIds(prev => new Set(prev).add(entryId));
        try {
            await timeClockService.rejectTimeEntry(userId, entryId, managerId, reason);
            toast.success('Time entry rejected');

            // Optimistic update
            setSummaries(prev => prev.map(s => {
                if (s.userId !== userId) return s;
                return {
                    ...s,
                    entries: s.entries.map(e => e.id === entryId ? { ...e, status: 'rejected' } : e),
                    pendingApproval: s.pendingApproval - 1
                };
            }));
        } catch (error) {
            console.error('Failed to reject entry:', error);
            toast.error('Failed to reject entry');
            loadTimesheets();
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(entryId);
                return next;
            });
        }
    };

    // Start editing
    const handleStartEdit = (entry: TimeEntry) => {
        setEditingEntry(entry);
        setEditForm({
            clockIn: new Date(entry.clockIn).toISOString().slice(0, 16),
            clockOut: entry.clockOut ? new Date(entry.clockOut).toISOString().slice(0, 16) : '',
            mileage: entry.mileage?.toString() || '',
            notes: entry.notes || '',
            reason: ''
        });
    };

    // Save edit
    const handleSaveEdit = async () => {
        if (!editingEntry || !editForm.reason) {
            toast.error('Please provide a reason for the edit');
            return;
        }

        try {
            await timeClockService.editTimeEntry(
                editingEntry.odId,
                editingEntry.id,
                managerId,
                {
                    clockIn: new Date(editForm.clockIn).toISOString(),
                    clockOut: editForm.clockOut ? new Date(editForm.clockOut).toISOString() : undefined,
                    mileage: parseFloat(editForm.mileage) || 0,
                    notes: editForm.notes
                },
                editForm.reason
            );

            toast.success('Time entry updated');
            setEditingEntry(null);
            loadTimesheets();
        } catch (error) {
            console.error('Failed to edit entry:', error);
            toast.error('Failed to update entry');
        }
    };

    // Delete entry
    const handleDelete = async (userId: string, entryId: string) => {
        if (!confirm('Are you sure you want to delete this time entry? This cannot be undone.')) {
            return;
        }

        try {
            await timeClockService.deleteTimeEntry(userId, entryId);
            toast.success('Time entry deleted');
            loadTimesheets();
        } catch (error) {
            console.error('Failed to delete entry:', error);
            toast.error('Failed to delete entry');
        }
    };

    // Bulk approve
    const handleBulkApprove = async () => {
        const pendingEntries: { userId: string; entryId: string }[] = [];

        summaries.forEach(s => {
            s.entries
                .filter(e => e.status === 'pending_approval')
                .forEach(e => {
                    pendingEntries.push({ userId: s.userId, entryId: e.id });
                });
        });

        if (pendingEntries.length === 0) {
            toast.info('No entries pending approval');
            return;
        }

        if (!confirm(`Approve all ${pendingEntries.length} pending entries?`)) {
            return;
        }

        setIsRefreshing(true);
        try {
            await timeClockService.bulkApproveEntries(pendingEntries, managerId);
            toast.success(`${pendingEntries.length} entries approved`);

            // Optimistic update
            setSummaries(prev => prev.map(s => {
                const userPending = pendingEntries.filter(p => p.userId === s.userId).map(p => p.entryId);
                if (userPending.length === 0) return s;

                return {
                    ...s,
                    entries: s.entries.map(e => userPending.includes(e.id) ? { ...e, status: 'approved' } : e),
                    pendingApproval: s.pendingApproval - userPending.length,
                    approved: s.approved + userPending.length
                };
            }));
        } catch (error) {
            console.error('Failed to bulk approve:', error);
            toast.error('Failed to approve entries');
            loadTimesheets();
        } finally {
            setIsRefreshing(false);
        }
    };

    // Export report
    const handleExportReport = () => {
        const report = `
MANAGER TIMESHEET REPORT
Generated: ${new Date().toLocaleString()}
Manager: ${managerName}
Period: ${dateRange === 'week' ? 'Last 7 Days' : dateRange === 'biweekly' ? 'Last 14 Days' : 'Last 30 Days'}

SUMMARY
=======
Total Hours: ${totals.totalHours.toFixed(2)}
Regular Hours: ${totals.regularHours.toFixed(2)}
Overtime Hours: ${totals.overtimeHours.toFixed(2)}
Total Mileage: ${totals.totalMileage.toFixed(1)} miles
Estimated Payroll: $${totals.estimatedPay.toFixed(2)}
Pending Approval: ${totals.pendingApproval}
Approved: ${totals.approved}

EMPLOYEE BREAKDOWN
==================
${summaries.map(s => `
${s.userName}
  Hours: ${s.totalHours.toFixed(2)} (Regular: ${s.regularHours.toFixed(2)}, OT: ${s.overtimeHours.toFixed(2)})
  Mileage: ${s.totalMileage.toFixed(1)} miles
  Est. Pay: $${s.estimatedPay.toFixed(2)}
  Entries: ${s.entriesCount} (${s.pendingApproval} pending, ${s.approved} approved)
`).join('\n')}
    `.trim();

        const blob = new Blob([report], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `timesheet-report-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Report downloaded');
    };

    const formatDateTime = (iso: string) => {
        const date = new Date(iso);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    return (
        <div className="fixed inset-0 z-50 bg-white dark:bg-slate-950 flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white px-4 py-4 shadow-lg flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="flex items-center space-x-2">
                            <Users className="h-6 w-6" />
                            <h2 className="text-xl font-bold">Manager Time Clock</h2>
                        </div>
                        <p className="text-sm text-indigo-100 mt-1">{managerName} • Employee Timesheets</p>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Button
                            onClick={loadTimesheets}
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 h-10 w-10"
                            disabled={isRefreshing}
                        >
                            <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                            onClick={onClose}
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 h-10 w-10"
                        >
                            <X className="h-6 w-6" />
                        </Button>
                    </div>
                </div>

                {/* Date Range Selector */}
                <div className="flex gap-2">
                    {(['week', 'biweekly', 'month'] as const).map((range) => (
                        <button
                            key={range}
                            onClick={() => setDateRange(range)}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${dateRange === range
                                ? 'bg-white text-indigo-700 dark:bg-slate-950 dark:text-indigo-300'
                                : 'bg-white/20 text-white hover:bg-white/30'
                                }`}
                        >
                            {range === 'week' ? '7 Days' : range === 'biweekly' ? '14 Days' : '30 Days'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-gray-50 dark:bg-slate-900 border-b dark:border-slate-800 flex-shrink-0 sticky top-0 z-10">
                <div className="flex overflow-x-auto w-full no-scrollbar">
                    {[
                        { id: 'overview', label: 'Overview', icon: BarChart3 },
                        { id: 'pending', label: `Pending (${totals.pendingApproval})`, icon: AlertTriangle },
                        { id: 'all', label: 'All Entries', icon: History },
                        { id: 'reports', label: 'Reports', icon: FileText },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as typeof activeTab)}
                            className={`flex-1 min-w-[100px] px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === tab.id
                                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 bg-white dark:bg-slate-950'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                        >
                            <tab.icon className="h-4 w-4 mx-auto mb-1" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <>
                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div className="p-4 space-y-4">
                                {/* Summary Cards - Redesigned with Gradients */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Total Hours - Cyan Gradient */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.1, type: "spring" }}
                                        whileHover={{ scale: 1.05, y: -4 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500 via-cyan-600 to-blue-500 p-3.5"
                                    >
                                        <div className="text-center">
                                            <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                                                <Clock className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <div className="text-lg mb-0.5 text-white">{totals.totalHours.toFixed(1)}</div>
                                            <div className="text-[9px] text-white/80">Total Hours</div>
                                        </div>
                                    </motion.div>

                                    {/* Est. Payroll - Emerald Gradient */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.2, type: "spring" }}
                                        whileHover={{ scale: 1.05, y: -4 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-500 p-3.5"
                                    >
                                        <div className="text-center">
                                            <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                                                <DollarSign className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <div className="text-lg mb-0.5 text-white">${totals.estimatedPay.toFixed(0)}</div>
                                            <div className="text-[9px] text-white/80">Est. Payroll</div>
                                        </div>
                                    </motion.div>

                                    {/* Pending Approval - Orange Gradient */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.3, type: "spring" }}
                                        whileHover={{ scale: 1.05, y: -4 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 p-3.5"
                                    >
                                        <div className="text-center">
                                            <motion.div
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6 }}
                                                animate={{
                                                    scale: totals.pendingApproval > 0 ? [1, 1.2, 1] : 1,
                                                    opacity: totals.pendingApproval > 0 ? [1, 0.7, 1] : 1
                                                }}
                                                style={{ animationDuration: '2s', animationIterationCount: 'infinite' }}
                                            >
                                                <AlertTriangle className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <div className="text-lg mb-0.5 text-white">{totals.pendingApproval}</div>
                                            <div className="text-[9px] text-white/80">Pending Approval</div>
                                        </div>
                                    </motion.div>

                                    {/* Employees - Indigo Gradient */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.4, type: "spring" }}
                                        whileHover={{ scale: 1.05, y: -4 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-600 p-3.5"
                                    >
                                        <div className="text-center">
                                            <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                                                <Users className="w-5 h-5 mx-auto mb-1.5 text-white" strokeWidth={2.5} />
                                            </motion.div>
                                            <div className="text-lg mb-0.5 text-white">{summaries.length}</div>
                                            <div className="text-[9px] text-white/80">Employees</div>
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Quick Actions */}
                                <div className="flex gap-2">
                                    {totals.pendingApproval > 0 && (
                                        <Button onClick={handleBulkApprove} className="flex-1 bg-green-600 hover:bg-green-700">
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            Approve All ({totals.pendingApproval})
                                        </Button>
                                    )}
                                    <Button onClick={handleExportReport} variant="outline" className="flex-1">
                                        <Download className="h-4 w-4 mr-2" />
                                        Export Report
                                    </Button>
                                </div>

                                {/* Employee List */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-center justify-between">
                                            <span className="flex items-center">
                                                <Users className="h-4 w-4 mr-2" />
                                                Employee Summary
                                            </span>
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <Input
                                                    type="text"
                                                    placeholder="Search..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="pl-8 h-8 w-40 text-sm"
                                                />
                                            </div>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <div className="divide-y dark:divide-slate-700">
                                            {filteredSummaries.map((summary) => (
                                                <div
                                                    key={summary.userId}
                                                    className="p-4 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer"
                                                    onClick={() => setSelectedEmployee(
                                                        selectedEmployee === summary.userId ? null : summary.userId
                                                    )}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                                                                <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-900 dark:text-white">{summary.userName}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {summary.userRole || 'Pumper'} • {summary.entriesCount} entries
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-right">
                                                                <p className="font-semibold text-gray-900 dark:text-white">{summary.totalHours.toFixed(1)}h</p>
                                                                <p className="text-xs text-gray-500">${summary.estimatedPay.toFixed(0)}</p>
                                                            </div>
                                                            {summary.pendingApproval > 0 && (
                                                                <Badge className="bg-yellow-100 text-yellow-800">
                                                                    {summary.pendingApproval} pending
                                                                </Badge>
                                                            )}
                                                            <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${selectedEmployee === summary.userId ? 'rotate-90' : ''
                                                                }`} />
                                                        </div>
                                                    </div>

                                                    {/* Expanded View */}
                                                    {selectedEmployee === summary.userId && (
                                                        <div className="mt-4 pl-13 space-y-2" onClick={(e) => e.stopPropagation()}>
                                                            <div className="grid grid-cols-3 gap-2 text-sm">
                                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                                                                    <p className="text-blue-600 dark:text-blue-400 text-xs">Regular</p>
                                                                    <p className="font-semibold">{summary.regularHours.toFixed(1)}h</p>
                                                                </div>
                                                                <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                                                                    <p className="text-orange-600 dark:text-orange-400 text-xs">Overtime</p>
                                                                    <p className="font-semibold">{summary.overtimeHours.toFixed(1)}h</p>
                                                                </div>
                                                                <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                                                    <p className="text-green-600 dark:text-green-400 text-xs">Mileage</p>
                                                                    <p className="font-semibold">{summary.totalMileage.toFixed(0)}mi</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Pending Tab */}
                        {activeTab === 'pending' && (
                            <div className="p-4 space-y-4">
                                {totals.pendingApproval === 0 ? (
                                    <div className="text-center py-12">
                                        <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">All Caught Up!</h3>
                                        <p className="text-gray-500 dark:text-gray-400">No pending entries to review</p>
                                    </div>
                                ) : (
                                    <>
                                        <Button onClick={handleBulkApprove} className="w-full bg-green-600 hover:bg-green-700">
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            Approve All {totals.pendingApproval} Entries
                                        </Button>

                                        {summaries.map((summary) => {
                                            const pendingEntries = summary.entries.filter(e => e.status === 'pending_approval');
                                            if (pendingEntries.length === 0) return null;

                                            return (
                                                <Card key={summary.userId}>
                                                    <CardHeader className="pb-2">
                                                        <CardTitle className="text-base flex items-center gap-2">
                                                            <User className="h-4 w-4" />
                                                            {summary.userName}
                                                            <Badge className="bg-yellow-100 text-yellow-800">
                                                                {pendingEntries.length} pending
                                                            </Badge>
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="p-0">
                                                        <div className="divide-y dark:divide-slate-700">
                                                            {pendingEntries.map((entry) => (
                                                                <div key={entry.id} className="p-4">
                                                                    <div className="flex items-start justify-between mb-2">
                                                                        <div>
                                                                            <p className="font-medium text-gray-900 dark:text-white">
                                                                                {formatDateTime(entry.clockIn)}
                                                                            </p>
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                                to {entry.clockOut ? formatDateTime(entry.clockOut) : 'In Progress'}
                                                                            </p>
                                                                        </div>
                                                                        <Badge className="bg-blue-100 text-blue-800">
                                                                            {entry.hoursWorked?.toFixed(2)}h
                                                                        </Badge>
                                                                    </div>

                                                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                                                                        <MapPin className="h-3 w-3" />
                                                                        <span className="truncate">{entry.clockInLocation.address}</span>
                                                                        {entry.mileage && entry.mileage > 0 && (
                                                                            <>
                                                                                <span>•</span>
                                                                                <Car className="h-3 w-3" />
                                                                                <span>{entry.mileage.toFixed(1)} mi</span>
                                                                            </>
                                                                        )}
                                                                    </div>

                                                                    {entry.notes && (
                                                                        <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-slate-800 p-2 rounded mb-3">
                                                                            {entry.notes}
                                                                        </p>
                                                                    )}

                                                                    <div className="flex gap-2">
                                                                        <Button
                                                                            onClick={() => handleApprove(summary.userId, entry.id)}
                                                                            size="sm"
                                                                            className="flex-1 bg-green-600 hover:bg-green-700"
                                                                            disabled={processingIds.has(entry.id)}
                                                                        >
                                                                            {processingIds.has(entry.id) ? (
                                                                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                                                            ) : (
                                                                                <Check className="h-4 w-4 mr-1" />
                                                                            )}
                                                                            Approve
                                                                        </Button>
                                                                        <Button
                                                                            onClick={() => handleReject(summary.userId, entry.id)}
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                                                                            disabled={processingIds.has(entry.id)}
                                                                        >
                                                                            <XCircle className="h-4 w-4 mr-1" />
                                                                            Reject
                                                                        </Button>
                                                                        <Button
                                                                            onClick={() => handleStartEdit(entry)}
                                                                            size="sm"
                                                                            variant="outline"
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        )}

                        {/* All Entries Tab */}
                        {activeTab === 'all' && (
                            <div className="p-4 space-y-4">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            type="text"
                                            placeholder="Search employees..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                    <Button onClick={loadTimesheets} variant="outline" size="icon">
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>

                                {filteredSummaries.map((summary) => (
                                    <Card key={summary.userId}>
                                        <CardHeader
                                            className="pb-2 cursor-pointer"
                                            onClick={() => setExpandedEntries(prev => {
                                                const next = new Set(prev);
                                                if (next.has(summary.userId)) {
                                                    next.delete(summary.userId);
                                                } else {
                                                    next.add(summary.userId);
                                                }
                                                return next;
                                            })}
                                        >
                                            <CardTitle className="text-base flex items-center justify-between">
                                                <span className="flex items-center gap-2">
                                                    <User className="h-4 w-4" />
                                                    {summary.userName}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-normal text-gray-500">
                                                        {summary.totalHours.toFixed(1)}h • ${summary.estimatedPay.toFixed(0)}
                                                    </span>
                                                    <ChevronDown className={`h-4 w-4 transition-transform ${expandedEntries.has(summary.userId) ? 'rotate-180' : ''
                                                        }`} />
                                                </div>
                                            </CardTitle>
                                        </CardHeader>

                                        {expandedEntries.has(summary.userId) && (
                                            <CardContent className="p-0">
                                                <div className="divide-y dark:divide-slate-700">
                                                    {summary.entries.map((entry) => (
                                                        <div key={entry.id} className="p-4">
                                                            <div className="flex items-start justify-between mb-2">
                                                                <div>
                                                                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                                                                        {formatDateTime(entry.clockIn)}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        to {entry.clockOut ? formatDateTime(entry.clockOut) : 'Active'}
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <Badge className={
                                                                        entry.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                            entry.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                                                                entry.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                                                    entry.status === 'edited' ? 'bg-blue-100 text-blue-800' :
                                                                                        'bg-gray-100 text-gray-800'
                                                                    }>
                                                                        {entry.hoursWorked?.toFixed(2)}h
                                                                    </Badge>
                                                                </div>
                                                            </div>

                                                            {entry.editedBy && (
                                                                <p className="text-xs text-blue-600 mb-2">
                                                                    Edited by manager • {entry.editReason}
                                                                </p>
                                                            )}

                                                            <div className="flex gap-2">
                                                                {entry.status === 'pending_approval' && (
                                                                    <>
                                                                        <Button
                                                                            onClick={() => handleApprove(summary.userId, entry.id)}
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="text-green-600"
                                                                        >
                                                                            <Check className="h-3 w-3" />
                                                                        </Button>
                                                                        <Button
                                                                            onClick={() => handleReject(summary.userId, entry.id)}
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="text-red-600"
                                                                        >
                                                                            <XCircle className="h-3 w-3" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                <Button
                                                                    onClick={() => handleStartEdit(entry)}
                                                                    size="sm"
                                                                    variant="outline"
                                                                >
                                                                    <Edit className="h-3 w-3" />
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleDelete(summary.userId, entry.id)}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-red-600"
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </CardContent>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Reports Tab */}
                        {activeTab === 'reports' && (
                            <div className="p-4 space-y-4">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base">Payroll Summary Report</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Period</span>
                                                <span className="font-medium">
                                                    {dateRange === 'week' ? 'Last 7 Days' :
                                                        dateRange === 'biweekly' ? 'Last 14 Days' : 'Last 30 Days'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Employees</span>
                                                <span className="font-medium">{summaries.length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Total Hours</span>
                                                <span className="font-medium">{totals.totalHours.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Regular Hours</span>
                                                <span className="font-medium">{totals.regularHours.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Overtime Hours</span>
                                                <span className="font-medium text-orange-600">{totals.overtimeHours.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Total Mileage</span>
                                                <span className="font-medium">{totals.totalMileage.toFixed(1)} miles</span>
                                            </div>
                                            <div className="flex justify-between border-t pt-2 mt-2">
                                                <span className="text-gray-900 dark:text-white font-semibold">Estimated Payroll</span>
                                                <span className="font-bold text-green-600">${totals.estimatedPay.toFixed(2)}</span>
                                            </div>
                                        </div>

                                        <Button onClick={handleExportReport} className="w-full">
                                            <Download className="h-4 w-4 mr-2" />
                                            Download Full Report
                                        </Button>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base">Approval Status</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                    <span className="text-sm">Approved</span>
                                                </div>
                                                <span className="font-semibold">{totals.approved}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                                    <span className="text-sm">Pending</span>
                                                </div>
                                                <span className="font-semibold">{totals.pendingApproval}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                    <span className="text-sm">Total Entries</span>
                                                </div>
                                                <span className="font-semibold">{totals.entriesCount}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Edit Modal */}
            {editingEntry && (
                <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md bg-white dark:bg-slate-900">
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <Edit className="h-5 w-5" />
                                    Edit Time Entry
                                </span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setEditingEntry(null)}
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Clock In</label>
                                <Input
                                    type="datetime-local"
                                    value={editForm.clockIn}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, clockIn: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Clock Out</label>
                                <Input
                                    type="datetime-local"
                                    value={editForm.clockOut}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, clockOut: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Mileage</label>
                                <Input
                                    type="number"
                                    value={editForm.mileage}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, mileage: e.target.value }))}
                                    step="0.1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Notes</label>
                                <Textarea
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                                    rows={2}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-red-600">
                                    Reason for Edit *
                                </label>
                                <Textarea
                                    value={editForm.reason}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                                    placeholder="Required: Explain why this entry is being edited..."
                                    rows={2}
                                    className="border-red-200"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleSaveEdit}
                                    className="flex-1"
                                    disabled={!editForm.reason}
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Changes
                                </Button>
                                <Button
                                    onClick={() => setEditingEntry(null)}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
